import React, { useEffect, useState } from "react";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 sm:px-6 transition-all duration-500 ${
            scrolled
              ? "glass shadow-[0_8px_32px_rgba(15,23,42,0.10)] h-16"
              : "bg-white/30 backdrop-blur-md border border-white/40 h-20"
          }`}
        >
          {/* Logo */}
          <button
            data-testid="header-logo-btn"
            onClick={() => handleNav("home")}
            className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 rounded-xl"
          >
            <div className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-white shadow-md ring-1 ring-orange-100 overflow-hidden">
              <img
                src={BRAND.logoUrl}
                alt="Happy Journey 4 U"
                className="h-full w-full object-contain p-1"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight text-left">
              <span className="font-playfair text-[1.05rem] font-bold text-slate-900 tracking-tight">
                Happy Journey <span className="text-brand-orange">4U</span>
              </span>
              <span className="text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
                Est. {BRAND.established} · Burhanpur
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="px-4 py-2 rounded-full text-[0.92rem] font-medium text-slate-700 hover:text-brand-ocean hover:bg-white/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* CTA pair + mobile toggle */}
          <div className="flex items-center gap-2">
            {/* WhatsApp primary CTA */}
            <a
              href={`https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent("Hi Happy Journey 4U! I'd like to plan a trip.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold shadow-[0_6px_20px_rgba(37,211,102,0.35)] hover:bg-[#1eb858] hover:shadow-[0_10px_28px_rgba(37,211,102,0.45)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
            {/* Call outline CTA */}
            <a
              href={`tel:${BRAND.phones[0].number.replace(/\s/g, "")}`}
              data-testid="header-call-btn"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-brand-ocean/30 text-brand-ocean text-sm font-semibold bg-white/50 hover:border-brand-ocean hover:bg-white/80 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ocean"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phones[0].number}
            </a>
            {/* Mobile-only contact CTA */}
            <button
              data-testid="header-contact-cta"
              onClick={() => handleNav("contact")}
              className="hidden sm:inline-flex md:hidden items-center gap-2 px-5 py-2.5 rounded-full bg-brand-orange text-white text-sm font-semibold shadow-[0_8px_24px_rgba(243,146,0,0.35)] hover:bg-brand-orangeDark transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
            >
              Contact Us
            </button>
            {/* Mobile menu toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full glass focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile sheet */}
        {open && (
          <div
            data-testid="mobile-nav"
            className="lg:hidden mt-2 glass rounded-2xl p-3 animate-fade-in-up"
          >
            <div className="flex flex-col">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => handleNav(l.id)}
                  className="text-left px-4 py-3 rounded-xl text-slate-800 hover:bg-white/70 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent("Hi Happy Journey 4U! I'd like to plan a trip.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <button
                  data-testid="mobile-contact-cta"
                  onClick={() => handleNav("contact")}
                  className="px-4 py-3 rounded-xl bg-brand-orange text-white font-semibold text-sm"
                >
                  Plan Trip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
